import { FC, memo, useCallback, useContext, useMemo, useState } from 'react'
import { Box, Divider, FormControl, FormControlLabel, Radio, RadioGroup, styled } from '@mui/material'
import { ArrowForward } from '@mui/icons-material'
import { firstLetterUpperCase } from '../../../../../../utils/utils'
import ButtonRounded from '../../../../../../components/Common/utils/button/ButtonRounded'
import { IntegracionesContext } from '../../../../context/integraciones.context'
import {
  AttrsMapear,
  NovedadActions,
  NovedadCamposAChequear,
  NovedadControl,
  NovedadControlEnum,
  NovedadDiferencias
} from './novedad-types'
import { useAppStore } from '../../../../../../store/app-store'
import { Novedad as NovedadResponse, ProductoFotosNuevas } from '../../../../types/responses-novedades'
import SnackBarUtils from '../../../../../../utils/SnackBarUtils'

const NewData = styled('span')({
  color: '#5983EF',
  fontWeight: 'bold'
})

const OldData = styled('span')({
  fontWeight: 'bold'
})

const Actions = memo(
  ({ onChange, control, attr, disabled, applyText = 'Aplicar', denyText = 'Descartar' }: NovedadActions) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) as NovedadControlEnum
        onChange(attr, value)
      },
      [onChange, attr]
    )

    return (
      <FormControl>
        <RadioGroup row onChange={handleChange} value={control[attr]}>
          <FormControlLabel
            sx={{ width: '120px' }}
            disabled={disabled}
            value={1}
            label={applyText}
            control={<Radio />}
          />
          <FormControlLabel
            sx={{ width: '120px' }}
            disabled={disabled}
            value={2}
            label={denyText}
            control={<Radio />}
          />
        </RadioGroup>
      </FormControl>
    )
  }
)

const Novedad = memo(({ productoAA1T, productoTienda, attr, control, onChange, loading }: NovedadDiferencias) => {
  if (attr === 'precio' && productoTienda.precio <= 0) return null
  if (productoAA1T[attr] === productoTienda[attr]) return null

  return (
    <>
      <Box display="flex" justifyContent="space-between" width="100%" paddingY="12px">
        <Box display="flex" alignItems="center" gap={1}>
          {firstLetterUpperCase(attr)}:{' '}
          <OldData>
            {attr === 'precio' && '$'}
            {productoAA1T[attr]}
          </OldData>{' '}
          <ArrowForward />{' '}
          <NewData>
            {attr === 'precio' && '$'}
            {productoTienda[attr]}
          </NewData>
        </Box>
        <Box>
          <Actions control={control} onChange={onChange} attr={attr} disabled={loading} />
        </Box>
      </Box>
      <Divider color="#a2a2a2" />
    </>
  )
})

interface Props {
  novedad: NovedadResponse
  control: NovedadControl
  setControl: React.Dispatch<React.SetStateAction<NovedadControl>>
}

const NovedadBody: FC<Props> = ({ novedad, control, setControl }) => {
  const [loading, setLoading] = useState(false)
  const { productoExterno: productoTienda, productoInterno: productoAA1T, productoExternoFotos } = novedad
  const codLocal = useAppStore((state) => state.codLocal)
  const { rejectNovedades: reject, updateNovedades: update } = useContext(IntegracionesContext)

  const handleChange = useCallback(
    (attr: NovedadCamposAChequear, value: NovedadControlEnum) => {
      setControl((prev) => ({ ...prev, [attr]: value }))
    },
    [setControl]
  )

  const fotosActuales = useMemo(() => {
    return productoAA1T.fotosProducto
  }, [productoAA1T])

  const fotosNuevas: ProductoFotosNuevas[] = useMemo(() => {
    return productoExternoFotos ? JSON.parse(productoExternoFotos) : []
  }, [productoExternoFotos])

  const handleConfirm = useCallback(async () => {
    setLoading(true)
    try {
      novedad.productoExterno = productoAA1T

      novedad.productoExterno.idProducto = productoTienda.idproducto
      novedad.productoInterno.idProducto = productoAA1T.idproducto

      novedad.productoExterno.nombre =
        control.nombre === NovedadControlEnum.IDLE
          ? novedad.productoExterno.nombre
          : control.nombre === NovedadControlEnum.Aplicar
          ? productoTienda.nombre
          : productoAA1T.nombre
      novedad.productoExterno.descripcion =
        control.descripcion === NovedadControlEnum.IDLE
          ? novedad.productoExterno.descripcion
          : control.descripcion === NovedadControlEnum.Aplicar
          ? productoTienda.descripcion
          : productoAA1T.descripcion
      novedad.productoExterno.precio =
        control.precio === NovedadControlEnum.IDLE
          ? novedad.productoExterno.precio
          : control.precio === NovedadControlEnum.Aplicar
          ? productoTienda.precio
          : productoAA1T.precio
      novedad.productoExternoFotos =
        control.fotos === NovedadControlEnum.Descartar
          ? JSON.stringify(fotosNuevas)
          : JSON.stringify(
              productoAA1T.fotosProducto.map((foto) => ({
                id: foto.idfotoProducto,
                src: foto.url,
                name: foto.titulo
              }))
            )

      await update(codLocal, [novedad])
      SnackBarUtils.success('Producto actualizado correctamente')
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, [control])

  const isValid = useMemo(
    () =>
      (control.descripcion !== NovedadControlEnum.IDLE ||
        novedad.productoExterno.descripcion === novedad.productoInterno.descripcion) &&
      (control.nombre !== NovedadControlEnum.IDLE ||
        novedad.productoExterno.nombre === novedad.productoInterno.nombre) &&
      (control.precio !== NovedadControlEnum.IDLE ||
        novedad.productoExterno.precio === novedad.productoInterno.precio ||
        novedad.productoExterno.precio <= 0) &&
      (control.fotos !== NovedadControlEnum.IDLE ||
        fotosActuales.every((foto) => fotosNuevas.some((fotoNueva) => foto.descripcion === fotoNueva.name))),
    [control, novedad, fotosActuales, fotosNuevas]
  )

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {AttrsMapear.map((attr, i) => (
        <Novedad
          key={i}
          onChange={handleChange}
          control={control}
          productoAA1T={productoAA1T}
          productoTienda={productoTienda}
          attr={attr}
          loading={loading}
        />
      ))}
      <Box display="flex" justifyContent="space-between" width="100%" paddingY="12px">
        <Box display="flex" flexDirection="column" gap="4px">
          {fotosActuales.length > 0 && (
            <Box display="flex" gap={1} alignItems="center">
              <OldData sx={{ width: '200%', maxWidth: '200px' }}>Imágenes actuales</OldData>
              {fotosActuales.map((foto) => (
                <img key={foto.orden} src={foto.url} alt={foto.descripcion} width={75} height={75} />
              ))}
            </Box>
          )}
          {fotosNuevas.length > 0 && (
            <Box display="flex" gap={1} alignItems="center">
              <NewData sx={{ width: '200%', maxWidth: '200px' }}>Imágenes nuevas</NewData>
              {fotosNuevas.map((foto) => (
                <img key={foto.id} src={foto.src} alt={foto.name} width={75} height={75} />
              ))}
            </Box>
          )}
        </Box>
        <Actions
          control={control}
          onChange={handleChange}
          attr={'fotos'}
          disabled={loading}
          applyText="Nuevas"
          denyText="Actuales"
        />
      </Box>
      <ButtonRounded
        disabled={loading || !isValid}
        onClick={handleConfirm}
        bgcolor="#5983EF"
        textColor="white"
        sx={{ mt: 2, width: 'fit-content', alignSelf: 'flex-end' }}
      >
        {loading ? 'Actualizando' : 'Confirmar'}
      </ButtonRounded>
    </Box>
  )
}

export default memo(NovedadBody)
